import { Badge, Card, SimpleGrid, Stack, Text } from '@mantine/core'
import { cloneElement } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { SelectableCard } from '@/components/ui-shared/SelectableCard/SelectableCard'
import {
  INTEGRATION_CONFIG,
  getIntegrationsForType
} from '@/components/vlm/vlmPipelineConfig'
import { IntegrationType, PipelineType } from '@/types/vlm'

type OutputIntegrationsSelectorProps = {
  pipelineType: PipelineType
  uploadDescriptions: boolean
  onUploadDescriptionsChange: (value: boolean) => void
  enableCloudResults: boolean
  onEnableCloudResultsChange: (value: boolean) => void
}

export const OutputIntegrationsSelector = ({
  pipelineType,
  uploadDescriptions,
  onUploadDescriptionsChange,
  enableCloudResults,
  onEnableCloudResultsChange
}: OutputIntegrationsSelectorProps) => {
  const intl = useIntl()
  const integrations = getIntegrationsForType(pipelineType)

  const isSelected = (type: IntegrationType) => {
    if (INTEGRATION_CONFIG[type].alwaysEnabled) return true
    if (type === 'cloud_vlm_results') return enableCloudResults
    if (type === 'cloud_event_enrichment') return uploadDescriptions
    return false
  }

  const handleToggle = (type: IntegrationType) => {
    if (type === 'cloud_vlm_results') {
      onEnableCloudResultsChange(!enableCloudResults)
    }
    if (type === 'cloud_event_enrichment') {
      onUploadDescriptionsChange(!uploadDescriptions)
    }
  }

  return (
    <SimpleGrid cols={{ base: 1, sm: 3 }}>
      {integrations.map((type) => {
        const { icon, labelId, descriptionId, alwaysEnabled } =
          INTEGRATION_CONFIG[type]
        const selected = isSelected(type)

        return (
          <SelectableCard
            key={type}
            height={160}
            isSelected={selected}
            onClick={alwaysEnabled ? undefined : () => handleToggle(type)}
          >
            <Card.Section py="lg" inheritPadding>
              <Stack gap="xs" align="center">
                {cloneElement(icon, { size: 32 })}

                <Text size="sm" fw={600} ta="center">
                  {intl.formatMessage({ id: labelId })}
                </Text>

                <Text size="xs" c="dimmed" ta="center">
                  {intl.formatMessage({ id: descriptionId })}
                </Text>
              </Stack>

              {alwaysEnabled && (
                <Badge pos="absolute" top={8} right={8} size="xs" color="green">
                  <FormattedMessage id="general.badges.required" />
                </Badge>
              )}
            </Card.Section>
          </SelectableCard>
        )
      })}

      {/* Empty cols to prevent cards from stretching full width */}
      <div />
      <div />
    </SimpleGrid>
  )
}
